import { TFunction } from 'react-i18next';
import {
  Brand,
  Candidate,
  Model,
  ModelsListResponse,
  NamingConvention,
} from '../api';
import { RadioOption } from '../components/RadioGroup/RadioGroup';
import { environment } from './environment';

export const formatTerminalLabels = (data: NamingConvention[]) => {
  return data
    .filter((term) => term.length > 0)
    .map((item, index) => {
      return { id: index.toString(), label: item };
    });
};

export const formatBrands = (data: Brand[]) => {
  return data
    .sort((a, b) => (a.brand_name > b.brand_name ? 1 : -1))
    .map((brand, index) => ({
      id: index.toString(),
      label: brand.brand_name,
      value: brand.brand_name,
    }));
};

export const formatCandidates = (
  data: ModelsListResponse,
  t: TFunction<'common', undefined>
) => {
  const uniqueMappings: RadioOption<Candidate>[] = [];
  const supportByHwVersion = data?.support_by_hw_version;
  let fullSupport = false;
  if (supportByHwVersion && environment.videoSupportEnabled) {
    fullSupport = Object.values(supportByHwVersion).some(
      (v) => v.audio === 'full_support'
    );
  }

  data.candidates.forEach((c) => {
    const candidate = { ...c };
    if (fullSupport) {
      candidate.support = 'full_support';
    }
    const label = candidate.terminal_labels.reduce(
      (acc, curr, i) => `${acc}${i === 0 ? '' : '/'}${curr}`,
      ''
    );

    const matchingMapping = uniqueMappings.find(
      (terminalMapping) => terminalMapping.label === label
    );

    if (matchingMapping) {
      if (matchingMapping.value.support !== candidate.support) {
        matchingMapping.value.support = 'possible_support';
      }
    } else {
      uniqueMappings.push({
        id: label,
        label,
        value: candidate,
      });
    }
  });

  const noneOfTheseLabel = t('buttons.noTerminalLabels');
  uniqueMappings.push({
    id: noneOfTheseLabel,
    label: noneOfTheseLabel,
    value: {
      model_name: 'none',
      support: 'possible_support',
      terminal_labels: [noneOfTheseLabel],
    },
  });
  return uniqueMappings;
};

export const formatModels = (data: Model[]) => {
  return data
    .sort((a, b) => (a.model_name > b.model_name ? 1 : -1))
    .map((model, index) => ({
      id: index.toString(),
      label: model.model_name,
      value: model.model_name,
    }));
};

export const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat(environment.locale?.replace('_', '-'), {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 1 !== 0 ? 2 : 0,
  }).format(amount);
